import ProductService from '../../api/services/ProductService';

export const namespaced = true;

export const state = {
  groups: [],
  group: {},
  style: {},
  connectors: [],
  loading: false,
  DYOTStyle: {},
  styleProductBundleSidebarElementLoading: false,
  replacementMunicipalPrice: null,
  replacementMunicipalPriceLoading: false,
  // `replacementMunicipalPrice` is null both before a fetch and after a failed one.
  // This flag is what lets the card tell "unknown" apart from "free".
  replacementMunicipalPriceError: false,
};

export const mutations = {
  SET_GROUPS(state, groups) {
    state.groups = groups;
  },
  SET_GROUP(state, group) {
    state.group = group;
  },
  SET_STYLE(state, style) {
    state.style = style;
  },
  SET_DYOT_STYLE(state, DYOTStyle) {
    state.DYOTStyle = DYOTStyle;
  },
  SET_CONNECTORS(state, connectors) {
    state.connectors = connectors;
  },
  LOADING(state, loading) {
    state.loading = loading;
  },
  SET_STYLE_PRODUCT_BUNDLE_SIDEBAR_ELEMENT_LOADING(state, loading) {
    state.styleProductBundleSidebarElementLoading = loading;
  },
  SET_STYLE_PRODUCT_BUNDLE_SIDEBAR_ELEMENT(state, contextObject) {
    state.style.productBundleSidebarElement = contextObject;
  },
  SET_REPLACEMENT_MUNICIPAL_PRICE(state, price) {
    state.replacementMunicipalPrice = price;
  },
  REPLACEMENT_MUNICIPAL_PRICE_LOADING(state, loading) {
    state.replacementMunicipalPriceLoading = loading;
  },
  SET_REPLACEMENT_MUNICIPAL_PRICE_ERROR(state, hasError) {
    state.replacementMunicipalPriceError = hasError;
  },
};

export const actions = {
  fetchGroups({ commit, dispatch }, { cityId }) {
    commit('LOADING', true);
    ProductService.getGroups(cityId)
      .then((response) => {
        commit('SET_GROUPS', response);
        // Everytime we fetch product groups, fetch DYOT style too
        dispatch('fetchDYOTStyle', { cityId });
        commit('LOADING', false);
      })
      .catch((error) => {
        commit('SET_GROUPS', []);
        commit('LOADING', false);
        error.location = 'fetchGroups';
        dispatch(
          'notification/notifyFailure',
          { message: 'There was an error loading our pawesome products.', options: { dismissSecs: 15 } },
          { root: true }
        );
      });
  },
  fetchGroup({ commit }, { id, cityId }) {
    commit('LOADING', true);
    ProductService.getGroup(id, cityId)
      .then((response) => {
        commit('SET_GROUP', response);
        commit('LOADING', false);
      })
      .catch((error) => {
        commit('SET_GROUP', {});
        commit('LOADING', false);
        error.location = 'fetchGroup';
      });
  },
  removeGroup({ commit }) {
    commit('SET_GROUP', {});
  },
  fetchReplacementTagStyle({commit, dispatch}, { id, cityId, petId }) {
    commit('LOADING', true);
    ProductService.getReplacementTagStyle(id, cityId, petId)
      .then((response) => {
        commit('SET_STYLE', response.replacementTags);
        commit('SET_CONNECTORS', response.connectors);
        dispatch('otherProduct/fetchGlowTagProduct', null, { root: true });
      })
      .catch((error) => {
        commit('SET_STYLE', {});
        commit('SET_CONNECTORS', []);
        error.location = 'fetchReplacementTagStyle';
        dispatch(
          'notification/notifyFailure',
          { message: 'There was an error loading this product.', options: { dismissSecs: 15 } },
          { root: true }
        );
      })
      .finally(() => commit('LOADING', false));
  },
  async fetchReplacementMunicipalPrice({ commit, dispatch }, { productStyleId, petId }) {
    commit('SET_REPLACEMENT_MUNICIPAL_PRICE_ERROR', false);

    if (!productStyleId || !petId) {
      commit('SET_REPLACEMENT_MUNICIPAL_PRICE', null);
      return;
    }

    commit('REPLACEMENT_MUNICIPAL_PRICE_LOADING', true);
    try {
      const { replacementTag } = await ProductService.getReplacementTagPrice(productStyleId, petId);
      commit('SET_REPLACEMENT_MUNICIPAL_PRICE', replacementTag?.discountedPrice || replacementTag?.price || null);
    } catch (error) {
      commit('SET_REPLACEMENT_MUNICIPAL_PRICE', null);
      commit('SET_REPLACEMENT_MUNICIPAL_PRICE_ERROR', true);
      error.location = 'fetchReplacementMunicipalPrice';
      dispatch(
        'notification/notifyFailure',
        { message: 'There was an error loading the replacement tag price.', options: { dismissSecs: 15 } },
        { root: true }
      );
    } finally {
      commit('REPLACEMENT_MUNICIPAL_PRICE_LOADING', false);
    }
  },
  async fetchProductBundleSidebarElement({ commit, dispatch }, { tagProductStyleId, tagProductId, includePersonalization}) {
    try {
      commit('SET_STYLE_PRODUCT_BUNDLE_SIDEBAR_ELEMENT_LOADING', true);
      const response = await ProductService.getProductBundleSidebarElement(tagProductStyleId, tagProductId, includePersonalization);
      commit('SET_STYLE_PRODUCT_BUNDLE_SIDEBAR_ELEMENT', response.productBundleSidebarElement);
    } catch (error) {
      error.location = 'fetchProductBundleSidebarElement';
      dispatch(
          'notification/notifyFailure',
          { message: 'There was an error loading Product Bundle details.', options: { dismissSecs: 15 } },
          { root: true }
      );
    } finally {
      commit('SET_STYLE_PRODUCT_BUNDLE_SIDEBAR_ELEMENT_LOADING', false);
    }
  },
  fetchStyle({ commit, dispatch }, { id, cityId }) {
    commit('LOADING', true);
    ProductService.getStyle(id, cityId)
      .then((response) => {
        commit('SET_STYLE', response.productStyle);
        commit('SET_CONNECTORS', response.connectors);
        dispatch('otherProduct/fetchGlowTagProduct', null, { root: true });
      })
      .catch((error) => {
        commit('SET_STYLE', {});
        commit('SET_CONNECTORS', []);
        error.location = 'fetchStyle';
        dispatch(
          'notification/notifyFailure',
          { message: 'There was an error loading this product.', options: { dismissSecs: 15 } },
          { root: true }
        );
      })
      .finally(() => commit('LOADING', false));
  },
  fetchDYOTStyle({ commit, dispatch }, { cityId }) {
    ProductService.getStyle(500, cityId)
      .then((response) => {
        commit('SET_DYOT_STYLE', response.productStyle);
      })
      .catch((error) => {
        commit('SET_DYOT_STYLE', {});
        error.location = 'fetchDYOTStyle';
        dispatch(
          'notification/notifyFailure',
          { message: "There was an error loading 'Design Your Own Tag product.'", options: { dismissSecs: 15 } },
          { root: true }
        );
      });
  },
  removeStyle({ commit }) {
    commit('SET_STYLE', {});
  },
};
